/**
 * API Headers Utility
 * 
 * Provides helper functions to add authentication headers to API requests
 */

import config from '../config';
import miaPlatformAuth from '../auth/MiaPlatformAuth';
import { buildAclContextHeaders } from './aclContext';

/**
 * Get headers for BFF API calls
 * Includes the secret header for authentication
 * Optionally includes Mia Platform user access token
 * 
 * @param {Object} additionalHeaders - Additional headers to merge
 * @param {boolean} includeMiaPlatformAuth - Whether to include Mia Platform user token
 * @returns {Object} Headers object with secret header
 */
export function getBffHeaders(additionalHeaders = {}, includeMiaPlatformAuth = false) {
  const headers = {
    'Accept': 'application/json',
    // Scope the request to the selected authz org/tenant (empty when none selected).
    ...buildAclContextHeaders(),
    ...additionalHeaders,
  };

  // Add secret header if API key is configured
  if (config.apiKeyBff) {
    headers['secret'] = config.apiKeyBff;
  }

  // Add Mia Platform access token if requested and available
  if (includeMiaPlatformAuth) {
    const miaPlatformToken = miaPlatformAuth.getAccessToken();
    if (miaPlatformToken) {
      headers['Authorization'] = `Bearer ${miaPlatformToken}`;
    }
  }

  return headers;
}

/**
 * Build a BFF URL with query parameters
 * Handles both absolute URLs (e.g., http://localhost:3000) and relative URLs (e.g., /)
 * 
 * @param {string} path - The path to append to bffUrl (e.g., '/bff/token-usage')
 * @param {Object} queryParams - Query parameters as key-value pairs
 * @returns {string} The constructed URL string
 */
export function buildBffUrl(path, queryParams = {}) {
  // Handle null/undefined bffUrl - default to empty string for relative URLs
  const baseUrl = config.bffUrl || '';
  const fullPath = `${baseUrl}${path}`;
  
  // Check if bffUrl is an absolute URL (starts with http)
  if (baseUrl.startsWith('http')) {
    // Absolute URL - use URL constructor
    const url = new URL(fullPath);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value.toString());
      }
    });
    return url.toString();
  } else {
    // Relative URL - construct query string manually
    const params = [];
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.push(`${key}=${encodeURIComponent(value.toString())}`);
      }
    });
    return params.length > 0 ? `${fullPath}?${params.join('&')}` : fullPath;
  }
}

/**
 * Get headers for Backend API calls
 * Includes the secret header for authentication
 * 
 * @param {Object} additionalHeaders - Additional headers to merge
 * @returns {Object} Headers object with secret header
 */
export function getBeHeaders(additionalHeaders = {}) {
  const headers = {
    'Accept': 'application/json',
    ...additionalHeaders,
  };

  // Add secret header if API key is configured
  if (config.apiKeyBe) {
    headers['secret'] = config.apiKeyBe;
  }

  // Authorization header is attached automatically by authtool's fetch
  // (via fetchRegistry). Do not inject tokens here.

  return headers;
}

/**
 * Get headers based on the URL being called
 * Automatically selects BFF or BE headers based on the URL
 * 
 * @param {string} url - The URL being called
 * @param {Object} additionalHeaders - Additional headers to merge
 * @returns {Object} Headers object with appropriate secret header
 */
export function getApiHeaders(url, additionalHeaders = {}) {
  if (url.includes(config.bffUrl)) {
    return getBffHeaders(additionalHeaders);
  } else if (url.includes(config.agentUrl)) {
    return getBeHeaders(additionalHeaders);
  }
  
  // Default to basic headers if URL doesn't match
  return {
    'Accept': 'application/json',
    ...additionalHeaders,
  };
}
